/**
 * Lista genérica de um recurso: largura total, busca e paginação. Substitui as telas
 * presas em `max-w-3xl` com lista chapada. A linha inteira leva à edição; o botão
 * âmbar "Criar" — o único primário da tela — abre o drawer.
 */
import { FileSpreadsheet, Plus, Search, Tag as TagIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FiltrosAvancados } from '@/components/crud/FiltrosAvancados';
import { useNavigate } from 'react-router-dom';
import { DrawerCriar } from '@/components/crud/DrawerCriar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/misc';
import { Paginacao } from '@/components/ui/paginacao';
import { Tabela, Tbody, Td, Th, Thead, Tr } from '@/components/ui/tabela';
import { api, type TagCad } from '@/lib/api';
import { formatar } from '@/lib/mascaras';
import type { DefColuna, DescritorRecurso, OwnerTipo, RecursoBase } from '@/lib/recursos/tipos';
import { useListaRecurso } from '@/lib/useListaRecurso';

//: Entidades cuja lista o backend filtra por tag (têm o parâmetro `tag`).
const FILTRAVEL_POR_TAG: OwnerTipo[] = ['pessoa', 'empresa', 'condominio'];

/** Dropdown de tags para filtrar a lista — carrega o catálogo uma vez. */
function FiltroTag({ valor, onChange }: { valor: string; onChange: (id: string) => void }) {
  const [tags, setTags] = useState<TagCad[]>([]);
  useEffect(() => {
    void api.get<TagCad[]>('/cadastro/tags').then((r) => r.ok && setTags(r.dados));
  }, []);
  return (
    <div className="relative">
      <TagIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-tertiary" />
      <select
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filtrar por tag"
        className="h-10 rounded-md border border-border bg-input pl-9 pr-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-(--focus-ring)"
      >
        <option value="">Todas as tags</option>
        {tags.map((t) => (
          <option key={t.id} value={t.id}>
            {t.nome}
          </option>
        ))}
      </select>
    </div>
  );
}

function celula(coluna: DefColuna<RecursoBase>, item: RecursoBase) {
  if (coluna.render) return coluna.render(item);
  const bruto = item[coluna.chave];
  if (bruto == null || bruto === '') return <span className="text-text-tertiary">—</span>;
  if (coluna.formato) return formatar(String(bruto), coluna.formato);
  return String(bruto);
}

export function ListaRecurso({ descritor }: { descritor: DescritorRecurso }) {
  const navegar = useNavigate();
  const { busca, buscar, tag, filtrarPorTag, filtros, aplicarFiltros, itens, total, carregando, pagina, porPagina, irParaPagina } =
    useListaRecurso<RecursoBase>(descritor.endpoint);
  const [criando, setCriando] = useState(false);
  const Icone = descritor.icone;
  const podeCriar = descritor.capacidades.podeCriar !== false;
  const filtravelPorTag = !!descritor.ownerTipo && FILTRAVEL_POR_TAG.includes(descritor.ownerTipo);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Icone className="size-5 text-accent" />
            <h1 className="tipo-titulo">{descritor.titulo}</h1>
          </div>
          <p className="mt-1 tipo-corpo-grande text-text-secondary">{descritor.descricao}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {descritor.importarPlanilha && (
            <Button
              variant="secundario"
              onClick={() => navegar(`/cadastro/importar/${descritor.chave}`)}
            >
              <FileSpreadsheet />
              {descritor.importarPlanilha.rotulo ?? 'Importar planilha'}
            </Button>
          )}
          {podeCriar && (
            <Button variant="primario" onClick={() => setCriando(true)}>
              <Plus />
              Criar {descritor.tituloSingular}
            </Button>
          )}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-tertiary" />
          <Input
            value={busca}
            onChange={(e) => buscar(e.target.value)}
            placeholder={descritor.buscaPlaceholder ?? 'Buscar'}
            className="pl-9"
          />
        </div>
        {filtravelPorTag && <FiltroTag valor={tag} onChange={filtrarPorTag} />}
        <FiltrosAvancados descritor={descritor} filtros={filtros} aoAplicar={aplicarFiltros} />
      </div>

      <Card>
        <CardContent className="p-0">
          {carregando && itens.length === 0 ? (
            <div className="flex flex-col gap-3 p-6">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-6 w-5/6" />
            </div>
          ) : itens.length === 0 ? (
            <div className="grid min-h-40 place-items-center py-10 text-center">
              <p className="tipo-corpo text-text-tertiary">
                {busca ? 'Nada encontrado para esta busca.' : `Nenhum registro ainda.`}
              </p>
            </div>
          ) : (
            <>
              <Tabela>
                <Thead>
                  {descritor.colunas.map((c) => (
                    <Th
                      key={c.chave}
                      alinhamento={c.alinhamento}
                      className={c.ocultarEm === 'sm' ? 'hidden sm:table-cell' : c.ocultarEm === 'md' ? 'hidden md:table-cell' : undefined}
                    >
                      {c.rotulo}
                    </Th>
                  ))}
                </Thead>
                <Tbody>
                  {itens.map((item) => (
                    <Tr key={item.id} onClick={() => navegar(`/cadastro/${descritor.chave}/${item.id}`)}>
                      {descritor.colunas.map((c) => (
                        <Td
                          key={c.chave}
                          mono={c.mono || !!c.formato}
                          alinhamento={c.alinhamento}
                          className={c.ocultarEm === 'sm' ? 'hidden sm:table-cell' : c.ocultarEm === 'md' ? 'hidden md:table-cell' : undefined}
                        >
                          {celula(c, item)}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Tabela>
              <Paginacao pagina={pagina} porPagina={porPagina} total={total} onMudar={irParaPagina} />
            </>
          )}
        </CardContent>
      </Card>

      {podeCriar && <DrawerCriar descritor={descritor} aberto={criando} onAbrir={setCriando} />}
    </div>
  );
}
